import type { Components, CssVarsTheme } from '@mui/material/styles';

const ALERT_COLORS = ['error', 'info', 'success', 'warning'] as const;

type AlertColor = (typeof ALERT_COLORS)[number];

const isAlertColor = (color: string | undefined): color is AlertColor =>
  color !== undefined && (ALERT_COLORS as readonly string[]).includes(color);

const styleOverrides: NonNullable<
  Components<CssVarsTheme>['MuiAlert']
>['styleOverrides'] = {
  root: ({ ownerState, theme }) => {
    const color = ownerState.color ?? ownerState.severity;
    if (!isAlertColor(color)) {
      return {};
    }

    const palette = theme.palette[color];

    return theme.applyStyles('light', {
      alignItems: 'center',
      '.MuiAlert-action': {
        alignItems: 'center',
        paddingTop: 0,
        paddingBottom: 0,
        marginRight: 0,
        '.MuiIconButton-root': {
          border: 0,
          boxShadow: 'none',
          outline: 'none',
        },
      },
      ...(ownerState.variant === 'standard' && {
        backgroundColor: palette.lightest,
        borderColor: palette.light,
        borderStyle: 'solid',
        borderWidth: '1px',
        '.MuiAlert-icon': {
          padding: 0,
          alignItems: 'center',
          color: palette.main,
        },
      }),
      ...(ownerState.variant === 'filled' && {
        backgroundColor: palette.main,
        color: palette.contrastText,
        '.MuiAlert-icon': {
          padding: 0,
          alignItems: 'center',
          color: palette.contrastText,
        },
      }),
      ...(ownerState.variant === 'outlined' && {
        backgroundColor: palette.lightest,
        borderColor: palette.main,
        '.MuiAlert-icon': {
          padding: 0,
          alignItems: 'center',
          color: palette.main,
        },
      }),
    });
  },
};

const MuiAlert: Components<CssVarsTheme>['MuiAlert'] = {
  styleOverrides,
};

export default MuiAlert;
